<template>
  <v-card>
    <v-toolbar flat>
      <div>
        <v-toolbar-title class="text-h6 font-weight-regular">Select options and map fields to match customers</v-toolbar-title>
      </div>
    </v-toolbar>
      <v-container class="ma-0 mp-0">
        <v-form ref="matchAddressForm" autocomplete="off">
          <v-card-text>
            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth">
                <p class="text-subtitle-1 font-weight-medium">1) Result Options:</p>
              </v-col>
              <v-col :cols="12 - ui.mappingDmsWidth">
                <v-radio-group v-model="includeCustomerDetails" class="mp-0 ma-0">
                  <v-radio label="Include customer details in the processed results" :value="true"></v-radio>
                  <v-radio label="Only set the customer_id in the processed results" :value="false"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>

            <!-- MAPPING -->
            <v-row no-gutters>
              <v-col cols="12">
                <p class="text-subtitle-1 font-weight-medium">2) Specify which CSV fields should be used to map the CSV data to DMS Data</p>
                <p><span class="font-italic font-weight-bold red--text">Warning: </span><span class="font-italic font-weight-bold">Do not </span>map the same CSV Column to different DMS columns otherwise you will end up with an error or unexpected results.</p>
              </v-col>
            </v-row>

            <!-- REQUIRED MAPPINGS -->
            <v-row no-gutters>
              <v-col cols="12">
                <p class="text-subtitle-1"><span class="font-weight-bold">Preferred</span><br/>
                Map the following</p>
              </v-col>
            </v-row>
            <!-- HEADER ROW -->
            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth" :offset="ui.mappingColOffset">
                <span class="text-subtitle-1 font-weight-bold">Dms Field</span>
              </v-col>
              <v-col :cols="ui.mappingDmsWidth">
                <span class="text-subtitle-1 font-weight-bold">CSV Field</span>
              </v-col>
            </v-row>
            <!-- REQUIRED FIELDS -->
            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth" :offset="ui.mappingColOffset">
                <v-subheader>Address Line 1</v-subheader>
              </v-col>
              <v-col :cols="ui.mappingCsvWidth">
                <v-autocomplete
                  autocomplete="off"
                  v-model="address_line_1"
                  :items="csvColumnNames"
                  required
                  :rules="vruleFieldRequired"
                  hint="*Required unless Full Address Mapping"
                  persistent-hint
                >
                </v-autocomplete>
              </v-col>
            </v-row>

           <!-- OPTIONAL FIELDS -->
            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth" :offset="ui.mappingColOffset">
                <v-subheader>Address Line 2</v-subheader>
              </v-col>
              <v-col :cols="ui.mappingCsvWidth">
                <v-autocomplete
                  autocomplete="off"
                  v-model="address_line_2"
                  :items="csvColumnNames"
                >
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth" :offset="ui.mappingColOffset">
                <v-subheader>City</v-subheader>
              </v-col>
              <v-col :cols="ui.mappingCsvWidth">
                <v-autocomplete
                  autocomplete="off"
                  v-model="city"
                  :items="csvColumnNames"
                  required
                  :rules="vruleFieldRequired"
                  hint="*Required unless Full Address Mapping"
                  persistent-hint
                >
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth" :offset="ui.mappingColOffset">
                <v-subheader>State</v-subheader>
              </v-col>
              <v-col :cols="ui.mappingCsvWidth">
                <v-autocomplete
                  autocomplete="off"
                  v-model="state"
                  :items="csvColumnNames"
                  required
                  :rules="vruleFieldRequired"
                  hint="*Required unless Full Address Mapping"
                  persistent-hint
                >
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth" :offset="ui.mappingColOffset">
                <v-subheader>Postal Code</v-subheader>
              </v-col>
              <v-col :cols="ui.mappingCsvWidth">
                <v-autocomplete
                  autocomplete="off"
                  v-model="postal_code"
                  :items="csvColumnNames"
                  required
                  :rules="vruleFieldRequired"
                  hint="*Required unless Full Address Mapping"
                  persistent-hint
                >
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- OPTIONAL MAPPINGS -->
           <v-row no-gutters>
              <v-col cols="12">
                <p class="text-subtitle-1"><span class="font-weight-bold">Optional/Secondary</span>
                <br/> Map <span class="font-italic">only</span> Full Address
                <span class="font-weight-bold">or</span> improve results by mapping
                <span class="font-italic">in addition to</span> Primary</p>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col :cols="ui.mappingDmsWidth" :offset="ui.mappingColOffset">
                <v-subheader>Full Address</v-subheader>
              </v-col>
              <v-col :cols="ui.mappingCsvWidth">
                <v-autocomplete
                  autocomplete="off"
                  v-model="full_address"
                  :items="csvColumnNames"
                  required
                  :rules="vruleFieldRequired"
                  hint="*Required if Alt Mapping"
                  persistent-hint
                >
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-form>
      </v-container>
  </v-card>
</template>

<script>
import validationMixin from '@/validators/validations_mixin'
import StepHelperMixin from './StepHelperMixin'
import { mapState, mapGetters, mapActions } from 'vuex'
import { hasValue } from '@/utils'
import { path } from 'ramda'

export default {
  name: 'match-address',
  mixins: [validationMixin, StepHelperMixin],
  data: function () {
    return {
      ui: {
        mappingDmsWidth: 2,
        mappingCsvWidth: 3,
        mappingColOffset: 1
      }
    }
  },
  computed: {
    ...mapState('CsvMappingStore', ['csvColumnNames', 'workflowSteps', 'transforms']),
    componentName () {
      return this.$options.name
    },
    mappingKey () {
      return 'mapping'
    },
    optionsKey () {
      return 'options'
    },
    address_line_1: {
      get () {
        return path([this.mappingKey, 'address_line_1'], this.mappingTransformData()(this.componentName))
      },
      set (val) {
        this.updateMappingTransformData({ componentName: this.componentName, argsKey: this.mappingKey, key: 'address_line_1', value: val })
        this.validateStep()
      }
    },
    address_line_2: {
      get () {
        return path([this.mappingKey, 'address_line_2'], this.mappingTransformData()(this.componentName))
      },
      set (val) {
        this.updateMappingTransformData({ componentName: this.componentName, argsKey: this.mappingKey, key: 'address_line_2', value: val })
        this.validateStep()
      }
    },
    city: {
      get () {
        return path([this.mappingKey, 'city'], this.mappingTransformData()(this.componentName))
      },
      set (val) {
        this.updateMappingTransformData({ componentName: this.componentName, argsKey: this.mappingKey, key: 'city', value: val })
        this.validateStep()
      }
    },
    state: {
      get () {
        return path([this.mappingKey, 'state'], this.mappingTransformData()(this.componentName))
      },
      set (val) {
        this.updateMappingTransformData({ componentName: this.componentName, argsKey: this.mappingKey, key: 'state', value: val })
        this.validateStep()
      }
    },
    postal_code: {
      get () {
        return path([this.mappingKey, 'postal_code'], this.mappingTransformData()(this.componentName))
      },
      set (val) {
        this.updateMappingTransformData({ componentName: this.componentName, argsKey: this.mappingKey, key: 'postal_code', value: val })
        this.validateStep()
      }
    },
    full_address: {
      get () {
        return path([this.mappingKey, 'full_address'], this.mappingTransformData()(this.componentName))
      },
      set (val) {
        this.updateMappingTransformData({ componentName: this.componentName, argsKey: this.mappingKey, key: 'full_address', value: val })
        this.validateStep()
      }
    },
    includeCustomerDetails: {
      get () {
        return path([this.optionsKey, 'include_customer_details'], this.mappingTransformData()(this.componentName))
      },
      set (val) {
        this.updateMappingTransformData({ componentName: this.componentName, argsKey: this.optionsKey, key: 'include_customer_details', value: val })
        this.validateStep()
      }
    }
  },
  methods: {
    ...mapActions('CsvMappingStore', ['updateMappingTransformData']),
    ...mapGetters('CsvMappingStore', ['mappingTransformData']),
    validateStep () {
      // valid if addr_l1 (opt l2) city, state & zip -or- full_address
      const addrParts = ['address_line_1', 'city', 'state', 'postal_code'].map(fieldName => hasValue(this[fieldName]))
      const addrPartsComplete = addrParts.reduce((accum, curVal) => {
        return accum && curVal
      }, true)
      this.setStepIsValid(this.full_address || addrPartsComplete)
    }
  }
}
</script>
